import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as Tone from 'tone';
import {
  Play, Pause, Square, Volume2, Search, Download, Settings, ArrowLeft,
  Drum, Clock, SkipBack, Repeat, Layers, ChevronLeft, ChevronRight,
  Sliders, Grid3X3, FileAudio, Code, Package, FolderUp, Save, ChevronUp, ChevronDown,
  Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Knob } from '@/components/batucada/Knob';
import { PianoKeyboard } from '@/components/batucada/PianoKeyboard';
import { InstrumentPackageBrowser } from '@/components/batucada/InstrumentPackageBrowser';
import { InstrumentVisual } from '@/components/batucada/InstrumentVisual';
import { ControlsSection } from '@/components/batucada/ControlsSection';
import { KontaktEditor } from '@/components/batucada/kontakt-tabs/KontaktEditor';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API = process.env.REACT_APP_BACKEND_URL;







// ============================================
// MAIN COMPONENT
// ============================================
function BatucadaRioInner() {
  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLooping, setIsLooping] = useState(true);
  const [bpm, setBpm] = useState(120);
  const [instruments, setInstruments] = useState([]);
  const [samples, setSamples] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeNotes, setActiveNotes] = useState([]);
  
  // Audio status
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [nowPlaying, setNowPlaying] = useState(null);
  const meterRef = useRef(null);

  // Control states
  const [isFixedVelocity, setIsFixedVelocity] = useState(true);
  const [close, setClose] = useState(0);
  const [over, setOver] = useState(0);
  const [room, setRoom] = useState(0);
  const [sync, setSync] = useState(0);
  const [offset, setOffset] = useState(0);
  const [masterVolume, setMasterVolume] = useState(80);
  
  // Key mappings - maps note (e.g. "C4") to sample
  const [keyMappings, setKeyMappings] = useState({});
  const keyPlayersRef = useRef({});
  
  // Editor state
  const [editingKey, setEditingKey] = useState(null);
  const [keyBuffers, setKeyBuffers] = useState({});
  const [keySettings, setKeySettings] = useState({});
  const [editorTab, setEditorTab] = useState('wave');
  const [groupSettings, setGroupSettings] = useState({
    1: { volume: 0, mute: false, solo: false },
    2: { volume: 0, mute: false, solo: false },
    3: { volume: 0, mute: false, solo: false },
    4: { volume: 0, mute: false, solo: false },
  });

  const handleGroupSettingsChange = (g, patch) => {
    setGroupSettings(prev => ({ ...prev, [g]: { ...prev[g], ...patch } }));
  };

  // Auto-persist group mixes & per-key settings (incl. scripts) per instrument
  const setupLoadedRef = useRef(false);
  useEffect(() => {
    if (!selectedInstrument || !setupLoadedRef.current) return;
    try {
      localStorage.setItem(
        `batucada_setup_${selectedInstrument.id}`,
        JSON.stringify({ groupSettings, keySettings })
      );
    } catch (e) { /* storage full - ignore */ }
  }, [groupSettings, keySettings, selectedInstrument]);
  
  // UI Panel states
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editorCollapsed, setEditorCollapsed] = useState(false);
  const [keyboardCollapsed, setKeyboardCollapsed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Refs
  const playerRef = useRef(null);
  const samplerRef = useRef(null);

  // Initialize audio meter
  useEffect(() => {
    meterRef.current = new Tone.Meter({ smoothing: 0.8 });
    Tone.getDestination().connect(meterRef.current);
    
    const updateMeter = () => {
      if (meterRef.current) {
        const level = meterRef.current.getValue();
        const normalizedLevel = Math.max(0, Math.min(100, (level + 60) * 1.67));
        setAudioLevel(normalizedLevel);
      }
      requestAnimationFrame(updateMeter);
    };
    updateMeter();
    
    return () => {
      if (meterRef.current) meterRef.current.dispose();
    };
  }, []);

  // Load instruments and samples from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [instrumentsRes, samplesRes] = await Promise.all([
          axios.get(`${API}/api/batucada/instruments`),
          axios.get(`${API}/api/batucada/samples`)
        ]);
        
        setInstruments(instrumentsRes.data);
        setSamples(samplesRes.data);
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load sample library');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle folder upload - folder name becomes instrument name
  const handleUploadFolder = async (files) => {
    // Get auth tokens
    const authToken = localStorage.getItem('auth_token');
    const basicAuth = sessionStorage.getItem('admin_auth');
    
    // Get the folder name from the first file's path
    const firstFile = files[0];
    const pathParts = firstFile.webkitRelativePath.split('/');
    const folderName = pathParts[0];
    
    // Filter for audio files only
    const audioExtensions = ['.wav', '.mp3', '.flac', '.aiff', '.ogg', '.m4a'];
    const audioFiles = Array.from(files).filter(file => {
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
      return audioExtensions.includes(ext);
    });
    
    if (audioFiles.length === 0) {
      toast.error('No audio files found in folder');
      return;
    }
    
    setIsUploading(true);
    
    try {
      toast.info(`Uploading ${audioFiles.length} files from "${folderName}"...`);
      
      // Build auth headers
      let headers = {};
      
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      } else if (basicAuth) {
        headers['Authorization'] = `Basic ${basicAuth}`;
      }
      
      // Find or use the "Uploads" ensemble
      const uploadsEnsembleId = '6a282f0a00419b19c2e1723f';
      
      // First, create the instrument with ensemble_id
      const instrumentRes = await axios.post(`${API}/api/batucada/instruments`, {
        name: folderName,
        ensemble_id: uploadsEnsembleId,
        description: `Uploaded folder with ${audioFiles.length} samples`
      }, { headers });
      
      const instrumentId = instrumentRes.data.id;
      let uploadedCount = 0;
      let failedCount = 0;
      
      // Upload each audio file as a sample (sequentially)
      for (let i = 0; i < audioFiles.length; i++) {
        const file = audioFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('instrument_id', instrumentId);
        
        try {
          await axios.post(`${API}/api/batucada/upload-sample`, formData, {
            headers: {
              ...headers,
              'Content-Type': 'multipart/form-data'
            }
          });
          uploadedCount++;
          
          // Update progress every 5 files
          if (uploadedCount % 5 === 0 || uploadedCount === audioFiles.length) {
            toast.info(`Uploaded ${uploadedCount}/${audioFiles.length} samples...`);
          }
        } catch (err) {
          failedCount++;
          console.error(`Failed to upload ${file.name}:`, err);
        }
      }
      
      if (uploadedCount > 0) {
        toast.success(`Created "${folderName}" with ${uploadedCount} samples!`);
      } else {
        toast.error(`Instrument created but no samples uploaded (${failedCount} failed)`);
      }
      
      // Reload data
      const [instrumentsRes, samplesRes] = await Promise.all([
        axios.get(`${API}/api/batucada/instruments`),
        axios.get(`${API}/api/batucada/samples`)
      ]);
      setInstruments(instrumentsRes.data);
      setSamples(samplesRes.data);
      
    } catch (err) {
      console.error('Folder upload error:', err);
      // Handle different error formats
      let errorMsg = 'Upload failed';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          errorMsg = detail.map(d => d.msg || d).join(', ');
        } else if (detail.msg) {
          errorMsg = detail.msg;
        }
      }
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle saving current mappings as a preset
  const handleSavePreset = async () => {
    if (Object.keys(keyMappings).length === 0) {
      toast.error('No samples mapped to save');
      return;
    }
    
    const presetName = prompt('Enter preset name:', selectedInstrument?.name || 'My Preset');
    if (!presetName) return;
    
    // Save to localStorage for now (could be saved to backend later)
    const preset = {
      name: presetName,
      instrument: selectedInstrument?.name,
      mappings: Object.entries(keyMappings).map(([key, data]) => ({
        key,
        sampleId: data.id,
        sampleName: data.name,
        url: data.url
      })),
      settings: keySettings,
      bpm,
      createdAt: new Date().toISOString()
    };
    
    // Get existing presets
    const existingPresets = JSON.parse(localStorage.getItem('batucada_presets') || '[]');
    existingPresets.push(preset);
    localStorage.setItem('batucada_presets', JSON.stringify(existingPresets));
    
    toast.success(`Preset "${presetName}" saved!`);
  };

  // Clear a single key mapping (doesn't delete the sample)
  const handleClearKey = (key) => {
    if (!key || !keyMappings[key]) return;
    
    const sampleName = keyMappings[key].name;
    
    // Remove from mappings
    setKeyMappings(prev => {
      const newMappings = { ...prev };
      delete newMappings[key];
      return newMappings;
    });
    
    // Remove from buffers
    setKeyBuffers(prev => {
      const newBuffers = { ...prev };
      delete newBuffers[key];
      return newBuffers;
    });
    
    // Remove from settings
    setKeySettings(prev => {
      const newSettings = { ...prev };
      delete newSettings[key];
      return newSettings;
    });
    
    // Clear from players ref
    delete keyPlayersRef.current[key];
    
    // Close editor
    setEditingKey(null);
    
    toast.success(`Cleared ${sampleName} from ${key}`);
  };

  // Clear all key mappings
  const handleClearAllKeys = () => {
    const count = Object.keys(keyMappings).length;
    if (count === 0) return;
    
    if (!confirm(`Clear all ${count} key mappings?`)) return;
    
    setKeyMappings({});
    setKeyBuffers({});
    setKeySettings({});
    keyPlayersRef.current = {};
    setEditingKey(null);
    setSelectedInstrument(null);
    
    toast.success(`Cleared ${count} key mappings`);
  };

  // Remap a sample from one key to another (for drag-and-drop in Mapping Editor)
  const handleRemapKey = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    
    const sourceMapping = keyMappings[sourceKey];
    const sourceBuffer = keyBuffers[sourceKey];
    const sourceSettings = keySettings[sourceKey];
    const sourcePlayer = keyPlayersRef.current[sourceKey];
    
    if (!sourceMapping) {
      toast.error('No sample to move');
      return;
    }
    
    // Check if target already has a mapping
    const targetMapping = keyMappings[targetKey];
    
    setKeyMappings(prev => {
      const newMappings = { ...prev };
      
      // If target has a mapping, swap them
      if (targetMapping) {
        newMappings[sourceKey] = targetMapping;
      } else {
        delete newMappings[sourceKey];
      }
      
      // Move source to target
      newMappings[targetKey] = sourceMapping;
      
      return newMappings;
    });
    
    setKeyBuffers(prev => {
      const newBuffers = { ...prev };
      const targetBuffer = prev[targetKey];
      
      if (targetBuffer) {
        newBuffers[sourceKey] = targetBuffer;
      } else {
        delete newBuffers[sourceKey];
      }
      
      if (sourceBuffer) {
        newBuffers[targetKey] = sourceBuffer;
      }
      
      return newBuffers;
    });
    
    setKeySettings(prev => {
      const newSettings = { ...prev };
      const targetSettings = prev[targetKey];
      
      if (targetSettings) {
        newSettings[sourceKey] = targetSettings;
      } else {
        delete newSettings[sourceKey];
      }
      
      if (sourceSettings) {
        newSettings[targetKey] = sourceSettings;
      }
      
      return newSettings;
    });
    
    // Update player refs
    const targetPlayer = keyPlayersRef.current[targetKey];
    if (targetPlayer) {
      keyPlayersRef.current[sourceKey] = targetPlayer;
    } else {
      delete keyPlayersRef.current[sourceKey];
    }
    if (sourcePlayer) {
      keyPlayersRef.current[targetKey] = sourcePlayer;
    }
    
    // Update editing key to follow the moved sample
    setEditingKey(targetKey);
    
    toast.success(`Moved ${sourceMapping.name} from ${sourceKey} to ${targetKey}`);
  };

  // Delete a sample from the database
  const handleDeleteSample = async (key, sample) => {
    if (!sample || !sample.id) {
      toast.error('No sample to delete');
      return;
    }
    
    if (!confirm(`Delete "${sample.name}" permanently? This cannot be undone.`)) return;
    
    const authToken = localStorage.getItem('auth_token');
    const basicAuth = sessionStorage.getItem('admin_auth');
    
    let headers = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    } else if (basicAuth) {
      headers['Authorization'] = `Basic ${basicAuth}`;
    }
    
    try {
      await axios.delete(`${API}/api/batucada/samples/${sample.id}`, { headers });
      
      // Clear from keyboard
      handleClearKey(key);
      
      // Reload samples
      const samplesRes = await axios.get(`${API}/api/batucada/samples`);
      setSamples(samplesRes.data);
      
      // Reload instruments (sample count may have changed)
      const instrumentsRes = await axios.get(`${API}/api/batucada/instruments`);
      setInstruments(instrumentsRes.data);
      
      toast.success(`Deleted "${sample.name}"`);
    } catch (err) {
      console.error('Delete sample error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete sample');
    }
  };

  // Delete an entire instrument (and all its samples)
  const handleDeleteInstrument = async (instrument) => {
    if (!instrument || !instrument.id) {
      toast.error('No instrument to delete');
      return;
    }
    
    const sampleCount = instrument.sample_count || 0;
    if (!confirm(`Delete "${instrument.name}" and all ${sampleCount} samples? This cannot be undone.`)) return;
    
    const authToken = localStorage.getItem('auth_token');
    const basicAuth = sessionStorage.getItem('admin_auth');
    
    let headers = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    } else if (basicAuth) {
      headers['Authorization'] = `Basic ${basicAuth}`;
    }
    
    try {
      await axios.delete(`${API}/api/batucada/instruments/${instrument.id}`, { headers });
      
      // If this was the selected instrument, clear the keyboard
      if (selectedInstrument?.id === instrument.id) {
        handleClearAllKeys();
      }
      
      // Reload instruments and samples
      const [instrumentsRes, samplesRes] = await Promise.all([
        axios.get(`${API}/api/batucada/instruments`),
        axios.get(`${API}/api/batucada/samples`)
      ]);
      setInstruments(instrumentsRes.data);
      setSamples(samplesRes.data);
      
      toast.success(`Deleted "${instrument.name}"`);
    } catch (err) {
      console.error('Delete instrument error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete instrument');
    }
  };

  // Initialize Tone.js
  useEffect(() => {
    Tone.Transport.bpm.value = bpm;
  }, [bpm]);

  // Filter instruments based on search and only show those with samples
  const filteredInstruments = instruments.filter(inst => 
    inst.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    (inst.sample_count || 0) > 0
  );

  // Handle selecting an instrument package - maps ALL its samples to keyboard
  const handleSelectInstrument = async (instrument) => {
    setupLoadedRef.current = false;
    setSelectedInstrument(instrument);
    
    // Start audio context
    await Tone.start();
    await Tone.context.resume();
    setIsAudioReady(true);
    
    // Get all samples for this instrument
    const instrumentSamples = samples
      .filter(s => s.instrument_id === instrument.id)
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort by name
    
    if (instrumentSamples.length === 0) {
      toast.info(`No samples found for ${instrument.name}`);
      return;
    }
    
    toast.info(`Loading ${instrumentSamples.length} samples for ${instrument.name}...`);
    
    // Define the keyboard keys to map to (starting from C2)
    const whiteNotes = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    const blackNotes = ['C#', 'D#', 'F#', 'G#', 'A#'];
    const octaves = [2, 3, 4, 5, 6];
    
    // Generate all keys in order
    const allKeys = [];
    octaves.forEach(octave => {
      whiteNotes.forEach((note, i) => {
        allKeys.push(`${note}${octave}`);
        // Add black key after C, D, F, G, A (not after E and B)
        if (i === 0) allKeys.push(`C#${octave}`);
        if (i === 1) allKeys.push(`D#${octave}`);
        if (i === 3) allKeys.push(`F#${octave}`);
        if (i === 4) allKeys.push(`G#${octave}`);
        if (i === 5) allKeys.push(`A#${octave}`);
      });
    });
    
    // Map samples to keys and prepare mappings first
    const newMappings = {};
    const loadTasks = [];
    
    for (let i = 0; i < instrumentSamples.length && i < allKeys.length; i++) {
      const sample = instrumentSamples[i];
      const key = allKeys[i];
      const fileUrl = sample.file_url;
      
      if (!fileUrl) continue;
      
      const url = fileUrl.startsWith('http') ? fileUrl : `${API}${fileUrl}`;
      const sampleName = sample.name.replace('.wav', '').replace('.mp3', '').replace('.flac', '');
      
      newMappings[key] = { 
        name: sampleName, 
        url, 
        sample,
        id: sample.id 
      };
      
      // Queue parallel pre-load task
      loadTasks.push(
        (async () => {
          try {
            const response = await fetch(url);
            if (response.ok) {
              const arrayBuffer = await response.arrayBuffer();
              const audioContext = Tone.getContext().rawContext;
              const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
              return { key, buffer: audioBuffer };
            }
          } catch (err) {
            console.error(`Failed to pre-load sample for ${key}:`, err);
          }
          return { key, buffer: null };
        })()
      );
    }
    
    // Load all buffers in parallel
    const loadResults = await Promise.all(loadTasks);
    const newBuffers = {};
    loadResults.forEach(({ key, buffer }) => {
      if (buffer) {
        keyPlayersRef.current[key] = buffer;
        newBuffers[key] = buffer;
      }
    });
    
    setKeyMappings(newMappings);
    setKeyBuffers(newBuffers);

    // Restore saved group mixes & scripts for this instrument
    let savedSetup = null;
    try {
      savedSetup = JSON.parse(localStorage.getItem(`batucada_setup_${instrument.id}`) || 'null');
    } catch (e) { savedSetup = null; }
    setKeySettings(savedSetup?.keySettings || {});
    if (savedSetup?.groupSettings) setGroupSettings(savedSetup.groupSettings);
    setupLoadedRef.current = true;
    setEditingKey(null);
    
    toast.success(`Loaded ${Object.keys(newMappings).length} samples to keyboard`);
    if (savedSetup && Object.keys(savedSetup.keySettings || {}).length > 0) {
      toast.success('Restored your saved group mixes & scripts');
    }
  };

  // Handle trim/loop changes from sample editor
  const handleTrimChange = (note, settings) => {
    setKeySettings(prev => ({
      ...prev,
      [note]: settings
    }));
    toast.success('Sample settings updated');
  };

  // Track currently playing players per note
  const activePlayersRef = useRef({});
  const noteDownTimeRef = useRef({});
  const ONE_SHOT_THRESHOLD = 200;

  // Piano key DOWN handler
  const handleNoteDown = async (note) => {
    await Tone.start();
    await Tone.context.resume();
    
    noteDownTimeRef.current[note] = Date.now();
    setActiveNotes(prev => [...prev.filter(n => n !== note), note]);
    
    if (activePlayersRef.current[note]) {
      try {
        activePlayersRef.current[note].player.stop();
        activePlayersRef.current[note].player.dispose();
      } catch (e) { /* ignore */ }
      delete activePlayersRef.current[note];
    }
    
    const mapping = keyMappings[note];
    if (mapping) {
      // Group gating (mute / solo)
      const noteSettings = keySettings[note];
      const grp = noteSettings?.group || 1;
      const g = groupSettings[grp] || {};
      const anySolo = Object.values(groupSettings).some(x => x.solo);
      if (g.mute || (anySolo && !g.solo)) {
        setActiveNotes(prev => prev.filter(n => n !== note));
        return;
      }
      try {
        let audioBuffer = keyPlayersRef.current[note];
        
        if (!audioBuffer) {
          const response = await fetch(mapping.url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          const arrayBuffer = await response.arrayBuffer();
          const audioContext = Tone.getContext().rawContext;
          audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          keyPlayersRef.current[note] = audioBuffer;
        }
        
        const player = new Tone.Player(audioBuffer).toDestination();
        player.loop = true;
        
        const settings = keySettings[note];
        const bufferDuration = audioBuffer.duration;
        
        if (settings) {
          player.loopStart = settings.loopStart || 0;
          player.loopEnd = settings.loopEnd || bufferDuration;
          player.start(undefined, settings.trimStart || 0);
        } else {
          const trimAmount = Math.min(0.03, bufferDuration * 0.02);
          player.loopEnd = bufferDuration - trimAmount;
          player.loopStart = 0;
          player.start();
        }
        
        player.volume.value = (masterVolume - 80) / 2 + (settings?.gainDb || 0) + (g.volume || 0);
        if (settings?.tune) {
          player.playbackRate = Math.pow(2, settings.tune / 12);
        }
        if (settings?.pan) {
          const panner = new Tone.Panner(Math.max(-1, Math.min(1, settings.pan / 100)));
          player.disconnect();
          player.chain(panner, Tone.getDestination());
        }
        activePlayersRef.current[note] = { player, isOneShot: false };
        
      } catch (err) {
        console.error(`Failed to play mapped sample for ${note}:`, err);
        toast.error(`Failed to play sample: ${err.message}`);
      }
    } else {
      if (!samplerRef.current) {
        samplerRef.current = new Tone.Synth().toDestination();
      }
      samplerRef.current.triggerAttack(note);
    }
  };

  // Piano key UP handler
  const handleNoteUp = (note, isMouseLeave = false) => {
    const downTime = noteDownTimeRef.current[note] || 0;
    const holdDuration = Date.now() - downTime;
    const isQuickTap = holdDuration < ONE_SHOT_THRESHOLD;
    
    const playerInfo = activePlayersRef.current[note];
    if (playerInfo) {
      if (isMouseLeave) {
        setActiveNotes(prev => prev.filter(n => n !== note));
        return;
      }
      
      if (isQuickTap) {
        playerInfo.player.loop = false;
        playerInfo.isOneShot = true;
        playerInfo.player.onstop = () => {
          if (activePlayersRef.current[note]?.player === playerInfo.player) {
            delete activePlayersRef.current[note];
            setActiveNotes(prev => prev.filter(n => n !== note));
          }
          try { playerInfo.player.dispose(); } catch(e) { /* ignore */ }
        };
      } else {
        setActiveNotes(prev => prev.filter(n => n !== note));
        try {
          playerInfo.player.stop();
          playerInfo.player.dispose();
          delete activePlayersRef.current[note];
        } catch (e) { /* ignore */ }
      }
    } else {
      setActiveNotes(prev => prev.filter(n => n !== note));
    }
    
    if (samplerRef.current) {
      try {
        samplerRef.current.triggerRelease();
      } catch (e) { /* ignore */ }
    }
    
    delete noteDownTimeRef.current[note];
  };

  // Play/Stop transport
  const togglePlay = async () => {
    await Tone.start();
    await Tone.context.resume();
    
    if (isPlaying) {
      Tone.Transport.stop();
      if (playerRef.current) {
        playerRef.current.stop();
      }
      setIsPlaying(false);
    } else {
      Tone.Transport.start();
      setIsPlaying(true);
    }
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
      }
      if (samplerRef.current) {
        samplerRef.current.dispose();
      }
      Object.values(activePlayersRef.current).forEach(info => {
        try { info.player?.stop(); info.player?.dispose(); } catch(e) { /* ignore */ }
      });
      activePlayersRef.current = {};
      noteDownTimeRef.current = {};
      keyPlayersRef.current = {};
    };
  }, []);

  return (
    <div className="h-screen bg-[#121212] text-white flex flex-col overflow-hidden">
      {/* AUDIO TEST BAR */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 p-2 flex items-center justify-center gap-6">
        <button
          onClick={async () => {
            try {
              await Tone.start();
              await Tone.context.resume();
              setIsAudioReady(true);
              
              const synth = new Tone.Synth().toDestination();
              synth.triggerAttackRelease("C4", "8n");
              toast.success("Audio enabled!");
              setNowPlaying("Test Beep");
              setTimeout(() => {
                setNowPlaying(null);
                synth.dispose();
              }, 2000);
            } catch (err) {
              toast.error("Audio error: " + err.message);
            }
          }}
          className="px-4 py-2 bg-yellow-400 text-black font-bold text-sm rounded-lg hover:bg-yellow-300 shadow-lg"
          data-testid="enable-audio-btn"
        >
          <PageEditable path="audio.enableBtn" as="span" fallback="ENABLE AUDIO" />
        </button>
        
        <div className="flex items-center gap-2">
          <span className="font-bold text-white text-sm">
            <PageEditable path="audio.statusLabel" as="span" fallback="Status:" />
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isAudioReady ? "bg-green-500 text-white" : "bg-gray-700 text-gray-300"}`}>
            {isAudioReady ? (
              <PageEditable path="audio.statusReady" as="span" fallback="READY" />
            ) : (
              <PageEditable path="audio.statusClick" as="span" fallback="Click to Enable" />
            )}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-sm">
            <PageEditable path="audio.levelLabel" as="span" fallback="Level:" />
          </span>
          <div className="w-24 h-4 bg-black/50 rounded overflow-hidden border border-white/30">
            <div 
              className="h-full bg-gradient-to-r from-green-500 to-yellow-500 transition-all duration-75"
              style={{ width: `${audioLevel}%` }}
            />
          </div>
        </div>
        
        {nowPlaying && (
          <div className="flex items-center gap-2 bg-black/30 px-3 py-1 rounded-full">
            <Volume2 className="w-4 h-4 text-yellow-400 animate-pulse" />
            <span className="text-yellow-400 font-bold text-sm">{nowPlaying}</span>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="h-12 bg-[#1a1a1a] border-b border-[#333] flex items-center px-4 gap-4">
        <Link to="/" className="p-2 hover:bg-[#333] rounded-lg" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        
        {/* Transport */}
        <div className="flex items-center gap-1 bg-black rounded-lg p-1">
          <button 
            onClick={() => {
              Tone.Transport.position = 0;
              if (playerRef.current) playerRef.current.seek(0);
            }}
            className="p-2 hover:bg-[#333] rounded"
            data-testid="rewind-btn"
          >
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            onClick={togglePlay}
            className={`p-2 rounded ${isPlaying ? 'bg-amber-500 text-black' : 'hover:bg-[#333]'}`}
            data-testid="play-btn"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={() => {
              Tone.Transport.stop();
              if (playerRef.current) playerRef.current.stop();
              setIsPlaying(false);
            }}
            className="p-2 hover:bg-[#333] rounded"
            data-testid="stop-btn"
          >
            <Square className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsLooping(!isLooping)}
            className={`p-2 rounded ${isLooping ? 'bg-amber-500/20 text-amber-400' : 'hover:bg-[#333]'}`}
            data-testid="loop-btn"
          >
            <Repeat className="w-4 h-4" />
          </button>
        </div>

        {/* BPM */}
        <div className="flex items-center gap-2 bg-black rounded-lg px-3 py-1">
          <Clock className="w-4 h-4 text-gray-500" />
          <input
            type="number"
            value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
            className="w-14 bg-transparent text-center font-mono text-amber-400 focus:outline-none"
            min={60}
            max={200}
            data-testid="bpm-input"
          />
          <span className="text-xs text-gray-500">BPM</span>
        </div>

        <div className="flex-1" />

        {/* Volume */}
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-gray-500" />
          <input
            type="range"
            min={0}
            max={100}
            value={masterVolume}
            onChange={(e) => setMasterVolume(Number(e.target.value))}
            className="w-24 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
            data-testid="master-volume"
          />
        </div>

        <Link 
          to="/admin/brazilian-grooves" 
          className="p-2 hover:bg-[#333] rounded-lg text-amber-400 hover:text-amber-300"
          title="Manage Sample Library"
          data-testid="manage-samples-btn"
        >
          <Settings className="w-5 h-5" />
        </Link>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Instrument Package Browser */}
        <InstrumentPackageBrowser
          instruments={filteredInstruments}
          selectedInstrument={selectedInstrument}
          onSelectInstrument={handleSelectInstrument}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          isLoading={isLoading}
          onUploadFolder={handleUploadFolder}
          isUploading={isUploading}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          onDeleteInstrument={handleDeleteInstrument}
          onClearKeyboard={handleClearAllKeys}
          mappedKeysCount={Object.keys(keyMappings).length}
        />

        {/* Main Content Area - Editor First */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Kontakt-style Editor with 5 Tabs - AT TOP */}
          <KontaktEditor
            selectedKey={editingKey}
            keyMappings={keyMappings}
            keyBuffers={keyBuffers}
            keySettings={keySettings}
            onTrimChange={handleTrimChange}
            onClose={() => setEditingKey(null)}
            activeTab={editorTab}
            onTabChange={setEditorTab}
            bpm={bpm}
            isCollapsed={editorCollapsed}
            onToggleCollapse={() => setEditorCollapsed(!editorCollapsed)}
            onSavePreset={handleSavePreset}
            onClearKey={handleClearKey}
            onClearAllKeys={handleClearAllKeys}
            onDeleteSample={handleDeleteSample}
            onRemapKey={handleRemapKey}
            groupSettings={groupSettings}
            onGroupSettingsChange={handleGroupSettingsChange}
          />

          {/* Visual Area - Collapsible background */}
          <InstrumentVisual 
            selectedInstrument={selectedInstrument}
            isPlaying={isPlaying}
          />

          {/* Controls */}
          <ControlsSection
            velocity={isFixedVelocity ? 100 : 50}
            onVelocityChange={() => {}}
            close={close}
            onCloseChange={setClose}
            over={over}
            onOverChange={setOver}
            room={room}
            onRoomChange={setRoom}
            sync={sync}
            onSyncChange={setSync}
            offset={offset}
            onOffsetChange={setOffset}
            isFixedVelocity={isFixedVelocity}
            onFixedVelocityToggle={() => setIsFixedVelocity(!isFixedVelocity)}
          />

          {/* Piano Keyboard - Collapsible at bottom */}
          <PianoKeyboard 
            onNoteDown={handleNoteDown}
            onNoteUp={handleNoteUp}
            activeNotes={activeNotes}
            keyMappings={keyMappings}
            onEditKey={setEditingKey}
            selectedKey={editingKey}
            isCollapsed={keyboardCollapsed}
            onToggleCollapse={() => setKeyboardCollapsed(!keyboardCollapsed)}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="h-8 bg-[#0a0a0a] border-t border-[#333] flex items-center justify-between px-4">
        <span className="text-xs text-gray-600">
          <PageEditable path="footer.left" as="span" fallback="Escola de Samba Casa Samba - Batucada Rhythm Creator" />
        </span>
        <span className="text-xs text-gray-600">
          <PageEditable path="footer.right" as="span" fallback="World Series" />
        </span>
      </div>
    </div>
  );
}

export default function BatucadaRio() {
  return (
    <PageEditProvider slug="batucada">
      <BatucadaRioInner />
      <PageEditToolbar />
      <AdminEditorLink to="/admin/batucada" label="Edit Batucada (Dashboard)" />
    </PageEditProvider>
  );
}
